import { useEffect, useLayoutEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';

/**
 * **Ein (i) zum Antippen** (Sascha, 8. Oktober, zur Exportqualität: „Was ist
 * das?“ -- und auf den Vorschlag einer Zeile darunter: „Lieber ein (i)
 * tooltip“). Ein `title` erscheint nur beim Überfahren mit der Maus, am
 * iPhone also nie. Das hier öffnet sich beim Antippen und geht beim Tippen
 * daneben, mit Escape oder einem zweiten Tippen wieder zu.
 *
 * Die Blase steht über dem Knopf, solange dort Platz ist -- und sonst
 * darunter. Steht der Knopf oben in einem Blatt, schnitt die Kopfzeile sie
 * ab: angetippt, blau, und nichts zu sehen (am ersten Foto bemerkt).
 */
export function InfoTipp({ label, children }: { label: string; children: ReactNode }): ReactElement {
  const [offen, setOffen] = useState(false);
  const [unten, setUnten] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const blase = useRef<HTMLSpanElement>(null);

  // Vor dem Malen nachmessen: passt sie über den Knopf, ohne dass der
  // sichtbare Teil des Blatts sie abschneidet? Sonst darunter.
  useLayoutEffect(() => {
    if (!offen || !ref.current || !blase.current) {
      setUnten(false);
      return;
    }
    const knopf = ref.current.getBoundingClientRect();
    const hoehe = blase.current.offsetHeight + 8;
    let oben = 0;
    let boden = window.innerHeight;
    for (let e = ref.current.parentElement; e; e = e.parentElement) {
      const ueberlauf = getComputedStyle(e).overflowY;
      if (ueberlauf === 'auto' || ueberlauf === 'scroll' || ueberlauf === 'hidden') {
        const r = e.getBoundingClientRect();
        oben = Math.max(oben, r.top);
        boden = Math.min(boden, r.bottom);
        break;
      }
    }
    const darueber = knopf.top - oben;
    const darunter = boden - knopf.bottom;
    setUnten(darueber < hoehe && darunter > darueber);
  }, [offen]);

  useEffect(() => {
    if (!offen) return;
    const weg = (e: PointerEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOffen(false);
    };
    const taste = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOffen(false);
    };
    document.addEventListener('pointerdown', weg);
    document.addEventListener('keydown', taste);
    return () => {
      document.removeEventListener('pointerdown', weg);
      document.removeEventListener('keydown', taste);
    };
  }, [offen]);

  return (
    <span className="info-tipp" ref={ref}>
      <button
        type="button"
        className={`info-knopf${offen ? ' active' : ''}`}
        aria-label={label}
        aria-expanded={offen}
        data-info-tipp
        onClick={() => setOffen((o) => !o)}
      >
        i
      </button>
      {offen ? (
        <span ref={blase} className={`info-blase${unten ? ' unten' : ''}`} role="tooltip" data-info-blase>
          {children}
        </span>
      ) : null}
    </span>
  );
}
